import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import api from "../services/api";

const PolicyPage = () => {
    const { slug } = useParams();
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/policies/${slug}`);
                setPolicy(response.data);
            } catch (error) {
                console.error("Error fetching policy:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPolicy();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
                <Navbar />
                <div className="flex justify-center items-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                </div>
            </div>
        );
    }

    if (!policy) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
                <Navbar />
                <div className="max-w-4xl mx-auto px-4 py-20 text-center">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Không tìm thấy trang</h1>
                    <p className="text-gray-600 dark:text-slate-400">Xin lỗi, chính sách bạn đang tìm kiếm không tồn tại.</p>
                </div>
            </div>
        );
    }

    return (
        <div 
          className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col relative overflow-hidden selection:bg-red-100 selection:text-red-900 transition-colors duration-500"
          style={{ 
            zoom: "0.8",
            MozTransform: "scale(0.8)",
            MozTransformOrigin: "top center"
          }}
        >
            <SEO 
                title={policy.title} 
                description={`Thông tin chi tiết về ${policy.title} tại TaHa Shoes. Chúng tôi cam kết minh bạch và đảm bảo quyền lợi tốt nhất cho khách hàng.`}
                keywords={`${policy.title}, chính sách taha shoes, quy định cửa hàng giày, taha shoes vn`}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": policy.title,
                    "description": policy.content.substring(0, 160),
                    "author": {
                        "@type": "Organization",
                        "name": "TaHa Shoes"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "TaHa Shoes",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://tahashoes.vn/logo.png"
                        }
                    },
                    "datePublished": new Date().toISOString()
                }}
            />
            <Navbar />
            
            {/* Decorative Background Elements */}
            <div className="absolute top-0 left-0 w-full h-[600px] pointer-events-none -z-10 overflow-hidden">
                <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-red-50 dark:bg-red-900/10 rounded-full blur-[120px] opacity-60 animate-pulse" />
                <div className="absolute top-[20%] left-[-5%] w-[300px] h-[300px] bg-blue-50 dark:bg-blue-900/10 rounded-full blur-[100px] opacity-40" />
                <div
                    className="absolute top-0 left-0 w-full h-full opacity-[0.03]"
                    style={{
                        backgroundImage: "radial-gradient(#dc2626 0.5px, transparent 0.5px)",
                        backgroundSize: "32px 32px",
                    }}
                />
            </div>

            <main className="flex-grow pt-24 md:pt-40 pb-12 sm:pb-20">
                {/* Hero section for Title */}
                <div className="max-w-4xl mx-auto px-4 mb-10 sm:mb-16">
                    <div className="text-center">
                        <div className="inline-block px-4 py-1.5 mb-6 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-xs font-bold tracking-widest uppercase border border-red-100 dark:border-red-900/30 shadow-sm">
                            Chính sách cửa hàng
                        </div>
                        <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white mb-6 tracking-tighter leading-tight">
                            {policy.title.split(' ').map((word, i) => (
                                <span key={i} className={i >= policy.title.split(' ').length - 2 ? "text-red-600" : ""}>
                                    {word}{' '}
                                </span>
                            ))}
                        </h1>
                        <div className="w-24 h-2 bg-gradient-to-r from-red-600 to-red-400 mx-auto rounded-full shadow-lg shadow-red-200 dark:shadow-red-900/20 mb-8" />
                    </div>
                </div>

                {/* Content section */}
                <div className="max-w-4xl mx-auto px-4">
                    <div className="bg-gradient-to-br from-sky-500 to-blue-600 dark:from-sky-600 dark:to-blue-800 rounded-[2.5rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.2)] p-8 sm:p-16 border border-blue-400/20 relative group overflow-hidden">
                        {/* Inner decoration */}
                        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                        
                        <div className="prose prose-lg max-w-none text-blue-50/90 leading-relaxed whitespace-pre-wrap font-medium relative z-10">
                            {policy.content}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PolicyPage;
